/**
 * Utility functions for error handling
 * Provides user-friendly error messages instead of raw status codes
 */

/**
 * Maps backend error messages to user-friendly messages with optional actions
 */
const AUTH_ERROR_MAP = {
  // Registration errors
  "Email already in use": {
    message: "This email is already registered.",
    type: "EMAIL_EXISTS",
    action: "login", // Suggest login instead
  },
  "Please provide name, email and password": {
    message: "Please fill in all required fields.",
    type: "VALIDATION_ERROR",
  },
  "Please provide a valid email": {
    message: "Please enter a valid email address.",
    type: "VALIDATION_ERROR",
  },
  "Password must be at least 8 characters": {
    message: "Password must be at least 8 characters long.",
    type: "VALIDATION_ERROR",
  },

  // Login errors
  "Invalid email or password": {
    message: "Incorrect email or password. Please try again.",
    type: "INVALID_CREDENTIALS",
  },
  "Please provide email and password": {
    message: "Please enter both email and password.",
    type: "VALIDATION_ERROR",
  },
  "Please verify your email before logging in": {
    message: "Please verify your email first.",
    type: "EMAIL_NOT_VERIFIED",
    action: "resend", // Suggest resending verification code
  },

  // Verification errors
  "Please provide email and verification code": {
    message: "Please enter the verification code.",
    type: "VALIDATION_ERROR",
  },
  "Invalid or expired verification code": {
    message: "The verification code is invalid or has expired.",
    type: "INVALID_CODE",
    action: "resend", // Suggest resending code
  },

  // Password reset errors
  "User not found": {
    message: "No account found with this email address.",
    type: "NOT_FOUND",
  },
  "Invalid or expired reset token": {
    message: "The reset link has expired. Please request a new one.",
    type: "INVALID_TOKEN",
  },

  // Email sending errors
  "Error sending verification email": {
    message: "Couldn't send verification email. Please try again.",
    type: "EMAIL_SEND_ERROR",
  },
  "Error sending password reset email": {
    message: "Couldn't send password reset email. Please try again.",
    type: "EMAIL_SEND_ERROR",
  },
};

/**
 * Check if a message contains a key phrase (partial match)
 * @param {string} message - The message to check
 * @returns {Object|null} Mapped error object or null
 */
const findPartialMatch = (message) => {
  if (!message) return null;

  const lowerMessage = message.toLowerCase();

  // Check for email verification requirement
  if (
    lowerMessage.includes("verify your email") ||
    lowerMessage.includes("email not verified")
  ) {
    return {
      message: "Please verify your email first.",
      type: "EMAIL_NOT_VERIFIED",
      action: "resend",
    };
  }

  // Check for password requirements
  if (lowerMessage.includes("password must be")) {
    return {
      message: message, // Keep the original message as it's descriptive
      type: "VALIDATION_ERROR",
    };
  }

  // Check for email already exists
  if (
    lowerMessage.includes("email already") ||
    lowerMessage.includes("already in use") ||
    lowerMessage.includes("already registered")
  ) {
    return {
      message: "This email is already registered.",
      type: "EMAIL_EXISTS",
      action: "login",
    };
  }

  // Check for invalid credentials
  if (
    lowerMessage.includes("invalid email") ||
    lowerMessage.includes("invalid password") ||
    lowerMessage.includes("incorrect")
  ) {
    return {
      message: "Incorrect email or password. Please try again.",
      type: "INVALID_CREDENTIALS",
    };
  }

  // Check for expired token/code
  if (lowerMessage.includes("expired") || lowerMessage.includes("invalid")) {
    if (lowerMessage.includes("verification") || lowerMessage.includes("code")) {
      return {
        message: "The verification code is invalid or has expired.",
        type: "INVALID_CODE",
        action: "resend",
      };
    }
    if (lowerMessage.includes("token") || lowerMessage.includes("reset")) {
      return {
        message: "The reset link has expired. Please request a new one.",
        type: "INVALID_TOKEN",
      };
    }
  }

  return null;
};

/**
 * Handle network errors consistently across the application
 * @param {Error} error - The error object from a failed API call
 * @returns {Object} Standardized error object with message, type, and optional action
 */
export const handleNetworkError = (error) => {
  // Default error message
  let result = {
    message: "An unexpected error occurred. Please try again.",
    type: "UNKNOWN",
    action: null,
  };

  // No internet connection
  if (!navigator.onLine) {
    return {
      message:
        "No internet connection. Please check your network and try again.",
      type: "NETWORK_OFFLINE",
      action: null,
    };
  }

  // IMPORTANT: Check if this is a pre-processed error from handleApiError
  // These have { message, status, data } structure instead of axios error structure
  if (error && error.status && error.message && !error.response) {
    const serverMessage = error.message;
    const status = error.status;

    // Check for exact match in our error map
    if (AUTH_ERROR_MAP[serverMessage]) {
      return {
        ...AUTH_ERROR_MAP[serverMessage],
        originalError: error,
      };
    }

    // Check for partial match
    const partialMatch = findPartialMatch(serverMessage);
    if (partialMatch) {
      return {
        ...partialMatch,
        originalError: error,
      };
    }

    // Handle status codes for pre-processed errors
    if (status === 401) {
      // Check if it's email verification issue
      if (serverMessage.toLowerCase().includes("verify")) {
        return {
          message: "Please verify your email first.",
          type: "EMAIL_NOT_VERIFIED",
          action: "resend",
          originalError: error,
        };
      }
      return {
        message: serverMessage || "Incorrect email or password. Please try again.",
        type: "UNAUTHORIZED",
        action: null,
        originalError: error,
      };
    }

    if (status === 409) {
      if (serverMessage.toLowerCase().includes("email")) {
        return {
          message: "This email is already registered.",
          type: "EMAIL_EXISTS",
          action: "login",
          originalError: error,
        };
      }
      return {
        message: serverMessage || "This resource already exists.",
        type: "CONFLICT",
        action: null,
        originalError: error,
      };
    }

    // For other pre-processed errors, use the message directly
    result.message = serverMessage;
    result.originalError = error;
    return result;
  }

  // Handle raw axios error structure
  if (error.response) {
    // Server responded with error status
    const status = error.response.status;
    const serverMessage = error.response.data?.message;

    // First, try to get the user-friendly message from our mapping
    if (serverMessage) {
      // Exact match
      if (AUTH_ERROR_MAP[serverMessage]) {
        return {
          ...AUTH_ERROR_MAP[serverMessage],
          originalError: error,
        };
      }

      // Partial match
      const partialMatch = findPartialMatch(serverMessage);
      if (partialMatch) {
        return {
          ...partialMatch,
          originalError: error,
        };
      }

      // If we have a server message but no mapping, use it as-is
      // (backend messages are already human-readable)
      result.message = serverMessage;
    }

    // Handle specific HTTP status codes as fallback
    switch (status) {
      case 400:
        result.type = "VALIDATION_ERROR";
        if (!serverMessage) {
          result.message = "Invalid input. Please check your information.";
        }
        break;

      case 401:
        result.type = "UNAUTHORIZED";
        if (!serverMessage) {
          result.message = "Authentication failed. Please sign in again.";
        }
        break;

      case 403:
        // Special handling for email verification
        if (error.response.data?.reason === "EMAIL_NOT_VERIFIED") {
          result.message = "Please verify your email first.";
          result.type = "EMAIL_NOT_VERIFIED";
          result.action = "resend";
        } else {
          result.type = "FORBIDDEN";
          if (!serverMessage) {
            result.message = "You don't have permission to access this resource.";
          }
        }
        break;

      case 404:
        result.type = "NOT_FOUND";
        if (!serverMessage) {
          result.message = "The requested resource was not found.";
        }
        break;

      case 409:
        result.type = "CONFLICT";
        if (!serverMessage) {
          result.message = "This resource already exists.";
        }
        // Common case: email already registered
        if (serverMessage?.toLowerCase().includes("email")) {
          result.message = "This email is already registered.";
          result.type = "EMAIL_EXISTS";
          result.action = "login";
        }
        break;

      case 422:
        result.type = "VALIDATION_ERROR";
        if (!serverMessage) {
          result.message = "Invalid input data. Please check your information.";
        }
        break;

      case 429:
        result.type = "RATE_LIMIT";
        const retryAfter = error.response.headers?.["retry-after"] || 60;
        result.message = `Too many attempts. Please wait ${retryAfter} seconds before trying again.`;
        break;

      default:
        if (status >= 500) {
          result.type = "SERVER_ERROR";
          result.message = "Server error. Please try again later.";
        }
    }
  } else if (error.request) {
    // Request made but no response received
    result.message = "No response from server. Please try again later.";
    result.type = "NO_RESPONSE";
  } else if (error.message) {
    // Something else caused the error
    // Check if it's a user-friendly message we can use
    const partialMatch = findPartialMatch(error.message);
    if (partialMatch) {
      return { ...partialMatch, originalError: error };
    }
    result.message = error.message;
  }

  result.originalError = error;
  return result;
};

/**
 * Handle authentication specific errors
 * @param {Error} error - The error object from a failed auth API call
 * @returns {Object} Processed error with appropriate user-friendly message and optional action
 */
export const handleAuthError = (error) => {
  const processedError = handleNetworkError(error);

  // Additional auth-specific handling can be added here if needed
  // The main error mapping is already handled by handleNetworkError

  return processedError;
};

/**
 * Get action hint for UI components
 * @param {string} action - The action type from error
 * @returns {Object} UI hints for the action
 */
export const getErrorActionHint = (action) => {
  switch (action) {
    case "login":
      return {
        text: "Login instead?",
        link: "/signin",
      };
    case "resend":
      return {
        text: "Resend code?",
        showResend: true,
      };
    case "reset":
      return {
        text: "Request new link",
        link: "/forgot-password",
      };
    default:
      return null;
  }
};
